import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { ArrowRightIcon } from 'lucide-react';
interface CaseStudy {
  title: string;
  slug: string;
  description: string;
  image: string;
  tags: string[];
  category: string;
}
const caseStudies: CaseStudy[] = [{
  title: 'US Bank Commercial Card',
  slug: 'us-bank',
  description: 'Lead UX Designer on enterprise B2B fintech platform. Achieved 98% user satisfaction and reduced processing time by 65% (54 days → 20 days) for corporate card applications.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png',
  tags: ['Digital Transformation', 'B2B', 'Growth'],
  category: 'FINANCIAL SERVICES'
}, {
  title: 'Cyber Security',
  slug: 'cyber-security',
  description: 'Product Designer for enterprise cybersecurity platform. Transformed complex threat analysis tools into intuitive B2B SaaS interface.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png',
  tags: ['Enterprise Software', 'B2B', 'SaaS'],
  category: 'DIGITAL PROTECTION'
}, {
  title: 'Case Western Reserve University',
  slug: 'case-western',
  description: 'Designed AI-powered faculty system achieving 85% accuracy and securing Amazon pilot program for 2026 rollout.',
  image: 'https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png',
  tags: ['AI Innovation', '0-to-1', 'Human Systems'],
  category: 'AI EXPLORATION'
}, {
  title: 'Watson vs Dialogflow',
  slug: 'ai-comparison',
  description: 'Product Designer for AI platform evaluation. Developed POCs using IBM Watson and Google Dialogflow, advising on stage 2 implementation that secured additional funding.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png',
  tags: ['AI Strategy', 'Enterprise Logic', 'Conversational Design'],
  category: 'AI COMPARISON'
}];
export function ParallaxCaseStudies() {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const handleImageClick = (slug: string) => {
    // Prevent navigation for Watson vs Dialogflow (coming soon)
    if (slug === 'ai-comparison') {
      return;
    }
    // CRITICAL: Set flag that user is navigating from home page
    sessionStorage.setItem('navigatedFromHome', 'true');
    sessionStorage.removeItem('cameFromChat'); // Clear chat flag if it exists
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
    navigate(`/case-study/${slug}`);
  };
  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    // Don't change cursor for Watson vs Dialogflow
    if (caseStudies[index].slug === 'ai-comparison') {
      if (window.cursorDefault) {
        window.cursorDefault();
      }
      return;
    }
    if (window.cursorSelectProject) {
      window.cursorSelectProject();
    }
  };
  const handleMouseLeave = () => {
    setHoveredIndex(null);
    if (window.cursorDefault) {
      window.cursorDefault();
    }
  };
  return <section className="relative w-full bg-black" id="projects" data-id="element-2032">
      {/* Sticky Header Container */}
      <div className="relative" data-id="element-2033">
        {/* Sticky Header */}
        <div className="sticky top-0 z-[100] bg-white w-full flex items-center justify-center h-[80px] shadow-[0_2px_4px_rgba(0,0,0,0.05)]" data-id="element-2034">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black uppercase leading-tight" style={{
          fontFamily: "'Gefika', sans-serif",
          fontWeight: 400,
          letterSpacing: '0.02em'
        }} data-id="element-2035">
            Case Studies
          </h2>
        </div>

        {/* Case Study Stack Container */}
        <div className="relative w-full" data-id="element-2036">
          {caseStudies.map((study, index) => {
          const isComingSoon = study.slug === 'ai-comparison';
          return <div key={study.slug} className="relative w-full h-screen overflow-hidden flex items-center" onClick={() => handleImageClick(study.slug)} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave} style={{
            cursor: isComingSoon ? 'default' : 'pointer'
          }} data-id="element-2037">
                {/* Background Image */}
                <div className="absolute inset-0 w-full h-full" data-id="element-2038">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-transform duration-700 ease-out" style={{
                objectPosition: study.slug === 'us-bank' ? 'top' : 'center',
                transform: hoveredIndex === index && !isComingSoon ? 'scale(1.05)' : 'scale(1)'
              }} data-id="element-2039" />

                  {/* FIXED: Darker gradient overlay on mobile for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent md:from-black/90 md:via-black/50" data-id="element-2040"></div>
                  <div className="absolute inset-0 bg-black/30 md:bg-black/20" data-id="element-2041"></div>
                </div>

                {/* Content Container - Left Aligned */}
                <div className="relative z-20 w-full max-w-[1400px] mx-auto px-6 sm:px-12 md:px-16 lg:px-24 h-full flex flex-col justify-center items-start pointer-events-none" data-id="element-2042">
                  <div className="max-w-xl lg:max-w-2xl" data-id="element-2043">
                    {/* Category */}
                    <p className="text-xs sm:text-sm md:text-sm lg:text-base font-medium uppercase mb-4 text-white md:text-white/60 tracking-[0.2em] reveal" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-2044">
                      {study.category}
                    </p>

                    {/* Title */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] mb-6 text-left reveal" style={{
                  fontFamily: "'Gefika', sans-serif",
                  fontWeight: 400
                }} data-id="element-2045">
                      {study.slug === 'us-bank' ? <>
                          US Bank
                          <br data-id="element-2046" />
                          Commercial Card
                        </> : study.slug === 'case-western' ? <>
                          Case Western
                          <br data-id="element-2047" />
                          Reserve University
                        </> : study.title}
                    </h3>

                    {/* Tags - FIXED: Better contrast on mobile */}
                    <div className="flex flex-wrap justify-start gap-2 mb-8 reveal lg:gap-3 lg:mb-10" data-id="element-2048">
                      {study.tags.map((tag, i) => <span key={i} className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-[10px] sm:text-xs md:text-xs lg:text-sm text-white uppercase tracking-wider md:bg-white/5 md:border-white/10 lg:px-4 lg:py-1.5" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-2049">
                          {tag}
                        </span>)}
                    </div>

                    {/* Description - Match hero p text size and font exactly */}
                    <p className="text-white leading-relaxed max-w-lg md:max-w-lg lg:max-w-xl text-left mb-10 lg:mb-12 reveal" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  letterSpacing: '0.01em'
                }} data-id="element-2050">
                      {study.description}
                    </p>

                    {/* Button - Coming Soon or View Case Study */}
                    {isComingSoon ? <div className="flex items-center gap-3 text-white/40 pointer-events-none lg:mt-12" data-id="element-2051">
                        <span className="text-xs sm:text-sm uppercase tracking-[0.15em] border-b border-white/20 pb-1 lg:text-base" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-2052">
                          coming soon
                        </span>
                      </div> : <button onClick={e => {
                  e.stopPropagation();
                  handleImageClick(study.slug);
                }} className="group flex items-center gap-3 text-white pointer-events-auto lg:mt-12" data-cursor-shrink-target="true" onMouseEnter={() => window.cursorDefault && window.cursorDefault()} onMouseLeave={() => hoveredIndex === index && window.cursorSelectProject && window.cursorSelectProject()} data-id="element-2053">
                        <span className="text-xs sm:text-sm uppercase tracking-[0.15em] border-b border-white/30 pb-1 group-hover:border-white transition-colors duration-300 lg:text-base" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-2054">
                          view case study
                        </span>
                        <ArrowRight className="w-4 h-4 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300 lg:w-5 lg:h-5" data-id="element-2055" />
                      </button>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}